<script setup>
import { ref, onMounted, nextTick } from 'vue';
import StockDialog from '@/components/dashboard/StockDialog.vue';
import AnButton from "@/components/common/AnButton.vue";
import { Column, DataTable } from "primevue";

const props = defineProps({
  setSection: Function,
  selectedScreener: Object
});

const isStockDialogOpen = ref(false);
const selectedStock = ref({});
const dynamicColumns = ref([]);
const animatedText = ref(null);
const animationComplete = ref(false);

const openStockDialog = (data) => {
  console.log('Clicked stock data:', data.name || data.stockName, data);
  selectedStock.value = data;
  isStockDialogOpen.value = true;
};

const animateHeaderText = async () => {
  const rationaleText = props.selectedScreener.screenerRationale;
  const words = rationaleText.split(' ');
  
  if (!animatedText.value) return;
  
  animatedText.value.textContent = '';
  animatedText.value.classList.remove('animation-complete');
  
  for (let i = 0; i < words.length; i++) {
    await new Promise(resolve => setTimeout(resolve, 130));
    animatedText.value.textContent += (i > 0 ? ' ' : '') + words[i];
  }
  
  // Wait a moment then hide the cursor
  await new Promise(resolve => setTimeout(resolve, 330));
  animatedText.value.classList.add('animation-complete');
  animationComplete.value = true;
};

onMounted(async () => {
  dynamicColumns.value = parseDynamicColumns(props.selectedScreener.screenerLogic);
  
  // Start text animation
  await nextTick();
  animateHeaderText();
  
  // Debug: Check what properties are available in the first stock
  if (props.selectedScreener.screenerStocks && props.selectedScreener.screenerStocks.length > 0) {
    console.log('First stock object properties:', Object.keys(props.selectedScreener.screenerStocks[0]));
    console.log('First stock data:', props.selectedScreener.screenerStocks[0]);
  }
});

const parseDynamicColumns = (logic) => {
  const columns = [];
  if (logic.includes('Mar_Cap')) columns.push('Market Cap');
  if (logic.includes('DMA')) columns.push('50 DMA', '200 DMA');
  if (logic.includes('RSI')) columns.push('RSI');
  return columns;
};
</script>

<template>

  <div class="section-container">
    <div class="header-section">
      <h2 class="screener-name">{{ selectedScreener.screenerName }}</h2>
      <div class="rationale-container">
        <span class="animated-text" ref="animatedText"></span>
      </div>
    </div>

    <DataTable
      :value="selectedScreener.screenerStocks"
      class="mt-20"
      :pt="{
        headerRow: { class: 'font-weight-500' },
      }"
      :paginator="true"
      :rows="5"
      :rowsPerPageOptions="[5, 10, 15, 20]"
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
    >
      <Column header="Name" class="font-weight-500 text-primary">
        <template #body="props">
          <div
            class="d-flex align-items-center gap-5 cursor-pointer"
            @click="openStockDialog(props.data)"
          >
            {{ props.data.name || props.data.stockName || 'N/A' }} <i class="pi pi-link" /></div
        ></template>
      </Column>
      <Column header="Symbol" field="symbol" />
      <Column header="CMP" field="price" />
      <Column header="Sector" field="sector" />
      <Column header="P/E" field="peRatio" />
      <Column header="ROCE" field="roce" />
      <Column header="52 Wk High/Low">
        <template #body="slotProps">
          {{ slotProps.data.fiftyTwoWeekHigh }} / {{ slotProps.data.fiftyTwoWeekLow }}
        </template>
      </Column>
      <Column header="Unrealized Profit/Loss">
        <template #body="props">
          {{ props.data.unrealizedProfitLoss }} %
        </template></Column>
      <Column v-for="col in dynamicColumns" :key="col" :header="col">
        <template #body="props">
          {{ props.data[col] }}
        </template>
      </Column>
    </DataTable>

    <div class="d-flex justify-center gap-20 mt-30">
      <AnButton
        label="Back"
        @click="() => setSection(1)"
        variant="tab"
      />
    </div>

    <StockDialog 
      v-if="isStockDialogOpen" 
      :isStockDialogOpen="isStockDialogOpen" 
      :stock="selectedStock"
      @update:isStockDialogOpen="isStockDialogOpen = $event"
    />
  </div>
</template>

<style scoped>
.header-section {
  text-align: left;
  margin-bottom: 60px;
  margin-top: -80px;
  padding: 10px 0;
}

.screener-name {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--an-text-dark);
  line-height: 1.4;
  margin-bottom: 10px;
}

.rationale-container {
  font-size: 1.1rem;
  font-weight: 400;
  color: black;
  line-height: 1.5;
  min-height: 1.7rem;
}

.animated-text {
  display: inline-block;
  position: relative;
}

.animated-text::after {
  content: '|';
  color: var(--an-primary);
  animation: blink 0.4s infinite;
  margin-left: 2px;
  opacity: 1;
}

.animated-text.animation-complete::after {
  animation: fade-out 0.5s ease-out forwards;
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

@keyframes fade-out {
  to { opacity: 0; }
}
</style>
